import { getRequestEvent } from "solid-js/web";
import { RIASRecordType, SubscriptionState, padId } from "~/lib";
import { runBatchGetCommand } from "./dynamoDB";

export const getCognitoProfile = async () => {
	"use server";

	const { getUserFromEvent } = await import("./auth.server");

	const user = await getUserFromEvent();

	return user;
};

export const getUserCertificatesServer = async (limit: number = 25) => {
	"use server";
	const { getUserFromEvent } = await import("./auth.server");
	const { getByUser, batchGet } = await import("./dynamoDB");

	const user = await getUserFromEvent(getRequestEvent());

	if (!user) return new Response("Unauthorized", { status: 401 });

	const c = await getByUser("CERTIFICATE", user.userId!, limit);

	const projectIds = Array.from(
		new Map(
			c?.items?.map(item => [
				item.parent,
				{ pk: `USER#${user.userId!}`, sk: item.parent },
			]),
		).values(),
	);
	const projects = await batchGet(projectIds);

	return { projects, certificates: c.items };
};

export const getUserSubscriptionsServer = async () => {
	"use server";

	const { getByUser } = await import("./dynamoDB");

	return await getByUser("SUBSCRIPTION");
};

export const getProjectCertificatesServer = async (projectId: number) => {
	"use server";

	const { runQueryCommand, RIASTABLE } = await import("./dynamoDB");

	const r = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiByParent",
		KeyConditionExpression: "parent = :parent AND begins_with(sk, :prefix)",
		ExpressionAttributeValues: {
			":parent": padId(projectId, RIASRecordType.PROJECT),
			":prefix": "CERTIFICATE#",
		},
		ScanIndexForward: false,
	});

	return r?.items ?? [];
};

export const getByUserQueryServer = async (
	rowType: string,
	limit:number = 25,
	skId:string | undefined = undefined,
	userId:number|undefined = undefined,
) => {
	"use server";

	const { getByUser } = await import("./dynamoDB");

	return await getByUser(rowType, userId, limit, skId);
};

export const getByRowTypeServer = async (
	rowType: string,
	Limit: number = 25,
	ProjectionExpression: string | undefined = undefined,
	expressionAttributesNames: Record<string, any> | undefined = undefined,
	ExclusiveStartKey = undefined,
	ScanIndexForward: boolean = false,
) => {
	"use server";

	//console.log( "getByRowTypeServer", { rowType, Limit, ProjectionExpression, expressionAttributesNames, ExclusiveStartKey } );

	const { runQueryCommand, RIASTABLE } = await import("./dynamoDB");

	return await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiByType",
		KeyConditionExpression: "recordType = :type",
		ExpressionAttributeValues: { ":type": rowType },
		...(expressionAttributesNames && Object.keys(expressionAttributesNames).length
			? { ExpressionAttributeNames: expressionAttributesNames }
			: {}),
		ScanIndexForward,
		Limit,
		ProjectionExpression,
		ExclusiveStartKey,
	});
};

export const getProfileQueryServer = async (profileId?: number) => {
	"use server";

	const { getProfile } = await import("./dynamoDB");

	if (!profileId || profileId <= 0 || profileId == {}) {
		return null;
	}
	if (profileId > 0) {
		try {
			const r = await getProfile(profileId);
			return r;
		} catch (e) {
			console.log("getProfileQuery err", e);
		}
	}

	return null;
};

export const getCertifiersQueryServer = async (
	schemes: string[] = ["section-6-domestic", "section-6-non-domestic", "section-6-non-domestic-advanced"],
	statuses: string[] = ["active"],
	withSubs: boolean = true,
) => {
	"use server";

	const { runBatchGetCommand } = await import("./dynamoDB");

	// 1. Get subscriptions that match the schemes and statuses
	const subs = await Promise.all(
		statuses.map(status =>
			runRecordByStatusQueryServer("SUBSCRIPTION", status).then(r =>
				r.items.filter(s => schemes.includes(s.scheme))
			)
		)
	).then(results => results.flat());

	// 3. Build unique ApprovedBody keys
	const approvedBodyKeys = [...new Set(subs.map(s => s.approvedBodyId))].map(
		id => ({
			pk: `APPROVEDBODY#${id}`,
			sk: `APPROVEDBODY#${String(id).padStart(7, "0")}`,
		}),
	);

	// 4. Batch get ApprovedBodies
	const approvedBodies = await runBatchGetCommand(approvedBodyKeys).then(r =>
		r?.reduce((acc, item) => {
			acc[item.pk] = item;
			return acc;
		}, {}),
	);

	const subsByUserPk = subs.reduce((acc, sub) => {
		if (!acc[sub.pk]) acc[sub.pk] = [];
		acc[sub.pk].push({
			...sub,
			approvedBody:
				approvedBodies?.[`APPROVEDBODY#${sub.approvedBodyId}`] || null,
		});
		return acc;
	}, {} );
	
	// 5. Build unique User keys
	const userKeys = [...new Set(subs.map(s => s.pk))].map(
		pk => ({
			pk,
			sk: padId(Number(pk.split('#')[1]), RIASRecordType.USER),
		}),
	);
	
	// 6. Batch get Users
	const users = await runBatchGetCommand( userKeys );
	
	users?.map( user => {
		user.subs = subsByUserPk[user.pk] || [];
		return user;
	} );
	

	return users;
};

export const getAdminSubscriptionsServer = async () => {
	"use server";

	const { runBatchGetCommand } = await import("./dynamoDB");

	const subsRaw = await getByRowTypeServer(
		RIASRecordType.SUBSCRIPTION,
		9999,
		"pk, sk, #name, #status, expires, joined, scheme, approvedBodyId",
		{ "#name": "name", "#status": "status" },
	);

	const subs = subsRaw?.items ?? [];

	const approvedBodyKeys = [...new Set(subs.filter(s => s.approvedBodyId).map(s => s.approvedBodyId))].map(
		id => ({ pk: `APPROVEDBODY#${id}`, sk: `APPROVEDBODY#${String(id).padStart(7, "0")}` }),
	);

	const approvedBodies = approvedBodyKeys.length
		? await runBatchGetCommand(approvedBodyKeys, "pk, #name, #address", { "#name": "name", "#address": "address" })
		: [];

	const abByPk = (approvedBodies ?? []).reduce((acc, ab) => {
		acc[ab.pk] = ab;
		return acc;
	}, {} as Record<string, any>);

	const userKeys = [...new Set(subs.map(s => s.pk))].map(pk => ({
		pk,
		sk: padId(Number(pk.split("#")[1]), RIASRecordType.USER),
	}));

	const users = userKeys.length
		? await runBatchGetCommand(userKeys, "pk, userId, #name", { "#name": "name" })
		: [];

	const userByPk = (users ?? []).reduce((acc, u) => {
		acc[u.pk] = u;
		return acc;
	}, {} as Record<string, any>);

	const certsRaw = await getByRowTypeServer(
		RIASRecordType.CERTIFICATE,
		9999,
		"subscriptionId, created",
		undefined,
	);

	const certStatsById = (certsRaw?.items ?? []).reduce((acc, c) => {
		if (c.subscriptionId == null) return acc;

		const key = String(c.subscriptionId);
		const created = Number(c.created) || 0;

		if (!acc[key]) acc[key] = { count: 0, mostRecent: 0 };
		acc[key].count++;
		if (created > acc[key].mostRecent) acc[key].mostRecent = created;

		return acc;
	}, {} as Record<string, { count: number; mostRecent: number }>);

	return subs.map(sub => {
		const subId = parseInt(sub.sk?.replace("SUBSCRIPTION#", "") ?? "0", 10);
		const certStats = certStatsById[String(subId)] ?? { count: 0, mostRecent: 0 };
		const approvedBody = sub.approvedBodyId ? abByPk[`APPROVEDBODY#${sub.approvedBodyId}`] : null;
		const user = userByPk[sub.pk];

		return {
			...sub,
			userId: user?.userId,
			userName: user?.name,
			approvedBodyName: approvedBody?.name ?? approvedBody?.address?.title ?? approvedBody?.address?.name ?? null,
			certificateCount: certStats.count,
			mostRecentCertificate: certStats.mostRecent || null,
		};
	});
};

export const getAdminApprovedBodiesServer = async () => {
	"use server";

	const abRaw = await getByRowTypeServer(
		RIASRecordType.APPROVEDBODY,
		9999,
		"pk, sk, #name, #address, #coordinator, #deputy, piRenewalDate",
		{ "#name": "name", "#address": "address", "#coordinator": "coordinator", "#deputy": "deputy" },
	);

	const bodies = abRaw?.items ?? [];

	const subsRaw = await getByRowTypeServer(
		RIASRecordType.SUBSCRIPTION,
		9999,
		"pk, approvedBodyId, #status",
		{ "#status": "status" },
	);

	const subs = (subsRaw?.items ?? []).filter(s => s.approvedBodyId);

	const activeUsersByAbId = subs.reduce((acc, s) => {
		if (s.status !== SubscriptionState.ACTIVE) return acc;

		const id = String(s.approvedBodyId);
		if (!acc[id]) acc[id] = new Set<string>();
		acc[id].add(s.pk);

		return acc;
	}, {} as Record<string, Set<string>>);

	return bodies.map(ab => {
		const id = ab.pk.replace("APPROVEDBODY#", "");

		return {
			...ab,
			id,
			userCount: activeUsersByAbId[id]?.size ?? 0,
			status: activeUsersByAbId[id]?.size ? "active" : "dormant",
		};
	});
};

export const getByPKQueryServer = async (pk: string, sk?: string) => {
	"use server";

	const { queryByPK } = await import("./dynamoDB");
	
	const r = await queryByPK(pk, sk);

	return r;
};

export const runRecordByStatusQueryServer = async (
	recordType: string,
	status: string,
	descending: boolean = false,
): Promise<{ items: any[]; lastKey: string | null }> => {
	"use server";

	const { runQueryCommand, RIASTABLE } = await import("./dynamoDB");

	const queryResult = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiByRecordStatus",

		KeyConditionExpression: "#status = :status AND #type = :type",
		ExpressionAttributeNames: {
			"#status": "status",
			"#type": "recordType",
		},
		ExpressionAttributeValues: {
			":status": status,
			":type": recordType,
		},
		ScanIndexForward: descending,
		ProjectionExpression: "pk, sk", // important
	});

	const keys = queryResult?.items;

	let items = [];

	if (keys && keys.length > 0) {
		const batchResult = await runBatchGetCommand(keys);
		items = batchResult;
	}

	return { items, lastKey: queryResult?.lastEvaluatedKey };
};
